/**
 * Startup crash containment — resolve theme, create MUI theme, heal storage, never white-screen.
 */

import {
  DEFAULT_THEME_MODE,
  DEFAULT_THEME_STYLE,
  RX_THEME_MODE_KEY,
  RX_THEME_STYLE_KEY,
  loadThemePreference,
  type ThemePreference,
} from "@/constants/theme-preference";
import {
  RECOVERY_BODY_PRIMARY,
  RECOVERY_BODY_SECONDARY,
  RECOVERY_PRIMARY_LABEL,
  RECOVERY_SECONDARY_LABEL,
  RECOVERY_TITLE,
} from "@/components/recovery/recovery-content";
import { createAppTheme } from "@/theme/create-theme";

// Bundled URL — available even when React cannot mount.
// eslint-disable-next-line @typescript-eslint/no-require-imports -- plain shell must resolve at bundle time
const RECOVERY_IMAGE_SRC = require("@/assets/recovery/tea-spill-premium.jpg") as string;

export type StartupThemeBootstrapResult = {
  ok: boolean;
  preference: ThemePreference;
  /** True when the first createAppTheme attempt failed and we healed + retried. */
  recovered: boolean;
};

function clearThemeStorage(
  storage: Pick<Storage, "removeItem"> | null | undefined,
): void {
  if (!storage) return;
  try {
    storage.removeItem(RX_THEME_STYLE_KEY);
    storage.removeItem(RX_THEME_MODE_KEY);
  } catch {
    // ignore
  }
}

const SAFE_DEFAULT: ThemePreference = {
  themeStyle: DEFAULT_THEME_STYLE,
  themeMode: DEFAULT_THEME_MODE,
};

/**
 * Resolve preference and prove `createAppTheme` succeeds.
 * On failure: clear theme keys, retry once with classic-amber / light.
 */
export function bootstrapThemeSafely(
  storage: Pick<Storage, "getItem" | "setItem" | "removeItem"> | null | undefined =
    typeof localStorage !== "undefined" ? localStorage : null,
): StartupThemeBootstrapResult {
  try {
    const preference = loadThemePreference(storage);
    createAppTheme(preference.themeMode, preference.themeStyle);
    return { ok: true, preference, recovered: false };
  } catch (error) {
    // eslint-disable-next-line no-console -- startup containment
    console.error("[startup] theme bootstrap failed; healing storage", error);
    clearThemeStorage(storage);
    try {
      createAppTheme(SAFE_DEFAULT.themeMode, SAFE_DEFAULT.themeStyle);
      return { ok: true, preference: SAFE_DEFAULT, recovered: true };
    } catch (retryError) {
      // eslint-disable-next-line no-console -- startup containment
      console.error("[startup] theme bootstrap retry failed", retryError);
      return { ok: false, preference: SAFE_DEFAULT, recovered: true };
    }
  }
}

export const RX_PANIC_ROOT_ID = "rx-panic-root";
export const REACT_RECOVERY_TEST_ID = "app-error-fallback";

type FallbackShellOptions = {
  /** @deprecated Prefer branded recovery image; kept for test overrides. */
  logoSrc?: string;
  testId?: string;
  retryTestId?: string;
  homeTestId?: string;
};

/** True when the React error boundary has already committed the branded recovery card. */
export function isReactRecoveryVisible(): boolean {
  if (typeof document === "undefined") return false;
  return document.querySelector(`[data-testid="${REACT_RECOVERY_TEST_ID}"]`) != null;
}

/**
 * Dedicated mount for imperative recovery shells. Never created inside `#root`.
 * Inserts as a sibling after `#root` when missing.
 */
export function ensurePanicRoot(): HTMLElement | null {
  if (typeof document === "undefined") return null;

  const existing = document.getElementById(RX_PANIC_ROOT_ID);
  if (existing) return existing;

  const panicRoot = document.createElement("div");
  panicRoot.id = RX_PANIC_ROOT_ID;

  const reactRoot = document.getElementById("root");
  if (reactRoot?.parentNode) {
    reactRoot.parentNode.insertBefore(panicRoot, reactRoot.nextSibling);
  } else if (document.body) {
    document.body.appendChild(panicRoot);
  } else {
    return null;
  }

  return panicRoot;
}

/** Remove any imperative shell so a healthy remount / React recovery is the only UI. */
export function clearPanicRoot(): void {
  if (typeof document === "undefined") return;
  const panicRoot = document.getElementById(RX_PANIC_ROOT_ID);
  if (!panicRoot) return;
  panicRoot.innerHTML = "";
  panicRoot.removeAttribute("style");
}

function applyPanicRootOverlay(panicRoot: HTMLElement): void {
  panicRoot.style.position = "fixed";
  panicRoot.style.inset = "0";
  panicRoot.style.zIndex = "2147483647";
}

/**
 * Non-React panic shell when even theme creation fails twice.
 * Writes into `#rx-panic-root` only — never mutates React's `#root`.
 */
export function renderMinimalFallbackShell(options?: FallbackShellOptions): void {
  paintBrandedRecoveryShell({
    testId: "startup-panic-fallback",
    retryTestId: "startup-panic-retry",
    homeTestId: "startup-panic-home",
    ...options,
  });
}

/**
 * Chunk / deployment mismatch recovery shell (same branded recovery experience).
 */
export function renderChunkUpdateShell(options?: FallbackShellOptions): void {
  paintBrandedRecoveryShell({
    testId: "chunk-update-fallback",
    retryTestId: "chunk-update-reload",
    homeTestId: "chunk-update-home",
    ...options,
  });
}

function paintBrandedRecoveryShell(
  options: Required<Pick<FallbackShellOptions, "testId" | "retryTestId" | "homeTestId">> &
    Pick<FallbackShellOptions, "logoSrc">,
): void {
  if (isReactRecoveryVisible()) return;

  const panicRoot = ensurePanicRoot();
  if (!panicRoot) return;

  const imageSrc = escapeHtmlAttr(options.logoSrc ?? RECOVERY_IMAGE_SRC);
  const title = escapeHtml(RECOVERY_TITLE);
  const bodyPrimary = escapeHtml(RECOVERY_BODY_PRIMARY);
  const bodySecondary = escapeHtml(RECOVERY_BODY_SECONDARY);
  const primaryLabel = escapeHtml(RECOVERY_PRIMARY_LABEL);
  const secondaryLabel = escapeHtml(RECOVERY_SECONDARY_LABEL);

  applyPanicRootOverlay(panicRoot);
  panicRoot.innerHTML = `
    <div role="alert" data-testid="${options.testId}" style="box-sizing:border-box;min-height:100vh;margin:0;padding:20px 6vw;display:flex;flex-direction:column;align-items:center;justify-content:center;font-family:system-ui,-apple-system,'Segoe UI',Roboto,sans-serif;background:#f7f1e8;color:#2f2a24;">
      <div style="box-sizing:border-box;width:88%;max-width:440px;padding:0;display:flex;flex-direction:column;align-items:stretch;text-align:center;overflow:hidden;background:#fdf8f1;border-radius:18px;box-shadow:0 6px 20px rgba(0,0,0,0.08);border:1px solid #e8dfd2;">
        <div style="position:relative;width:100%;height:240px;overflow:hidden;flex-shrink:0;">
          <img src="${imageSrc}" alt="" aria-hidden="true" style="display:block;width:100%;height:100%;object-fit:cover;object-position:center 18%;margin:0;padding:0;border:0;pointer-events:none;user-select:none;" />
        </div>
        <div style="box-sizing:border-box;display:flex;flex-direction:column;align-items:center;text-align:center;padding:22px 22px 26px;">
          <h1 style="margin:0 0 12px;font-size:1.75rem;font-weight:700;line-height:1.25;letter-spacing:-0.01em;color:#2f2a24;">${title}</h1>
          <p style="margin:0;max-width:22rem;font-size:0.95rem;line-height:1.5;color:#6b6258;">${bodyPrimary}</p>
          <p style="margin:8px 0 0;max-width:22rem;font-size:0.95rem;line-height:1.5;color:#6b6258;">${bodySecondary}</p>
          <div style="display:flex;flex-direction:column;align-items:stretch;gap:12px;width:100%;margin-top:22px;">
            <button type="button" data-testid="${options.retryTestId}" autofocus style="width:100%;border:none;border-radius:28px;padding:14px 28px;font-size:1rem;font-weight:700;color:#fff;cursor:pointer;background:linear-gradient(135deg,#c2410c,#9a3412);box-shadow:0 10px 28px rgba(194,65,12,0.35);">${primaryLabel}</button>
            <button type="button" data-testid="${options.homeTestId}" style="appearance:none;align-self:center;border:none;background:transparent;padding:10px 12px;font-size:0.95rem;font-weight:600;color:#6b6258;text-decoration:underline;text-underline-offset:3px;cursor:pointer;">${secondaryLabel}</button>
          </div>
        </div>
      </div>
    </div>
  `;

  const retryBtn = panicRoot.querySelector(`[data-testid='${options.retryTestId}']`);
  retryBtn?.addEventListener("click", () => {
    window.location.reload();
  });

  const homeBtn = panicRoot.querySelector(`[data-testid='${options.homeTestId}']`);
  homeBtn?.addEventListener("click", () => {
    window.location.href = "/";
  });

  if (retryBtn instanceof HTMLElement) {
    try {
      retryBtn.focus();
    } catch {
      // ignore
    }
  }
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function escapeHtmlAttr(value: string): string {
  return escapeHtml(value).replace(/'/g, "&#39;");
}

declare global {
  interface Window {
    __rxResetTheme?: () => void;
  }
}

/** Dev-only panic reset for QA / local debugging. */
export function installDevThemeResetHelper(): void {
  if (process.env.NODE_ENV !== "development") return;
  if (typeof window === "undefined") return;

  window.__rxResetTheme = () => {
    try {
      localStorage.removeItem(RX_THEME_STYLE_KEY);
      localStorage.removeItem(RX_THEME_MODE_KEY);
      localStorage.removeItem("persist:root");
    } catch {
      // ignore
    }
    window.location.reload();
  };
}
